import { Alert } from '@/components/nativewind-ui/alert';
import { OnboardingButton } from '@/components/onboarding-button';
import { OnboardingPagination } from '@/components/onboarding-pagination';
import { Text } from '@/components/ui/text';
import {
  type PersonaImport,
  PersonaImportContext
} from '@/contexts/PersonaImportContext';
import type { AlertRef } from '@/types/alert';
import { HeaderBackButton } from '@react-navigation/elements';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import {
  useGlobalSearchParams,
  useRouter,
  useSegments,
  withLayoutContext
} from 'expo-router';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Dimensions, StyleSheet, View } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';

const { Navigator } = createMaterialTopTabNavigator();

const MaterialTopTabs = withLayoutContext(Navigator);
const PAGES = ['persona-import', 'how', 'upload', 'preview', 'process'];
const PAGE_TITLES = {
  'persona-import': 'New Persona',
  how: 'How',
  upload: 'Upload',
  preview: 'Preview',
  process: 'Processing'
};

export default function PersonaImportLayout() {
  return (
    <SafeAreaView className="flex-1">
      <MaterialTopTabs
        initialRouteName="index"
        initialLayout={{
          width: Dimensions.get('window').width
        }}
        backBehavior="order"
        layout={(props) => {
          return <Layout {...props} />;
        }}
        tabBar={(props) => <TabBar {...props} />}
        screenOptions={{
          swipeEnabled: false,
          lazy: true
        }}
      >
        <MaterialTopTabs.Screen
          name="index"
          options={{
            title: 'New Persona'
          }}
        />
        <MaterialTopTabs.Screen
          name="how"
          options={{
            title: 'How'
          }}
        />
        <MaterialTopTabs.Screen
          name="upload"
          options={{
            title: 'Upload'
          }}
        />
        <MaterialTopTabs.Screen
          name="preview/[id]"
          options={{
            title: 'Preview'
          }}
        />
        <MaterialTopTabs.Screen
          name="process/[id]"
          options={{
            title: 'Process'
          }}
        />
      </MaterialTopTabs>
    </SafeAreaView>
  );
}

function Layout({
  children
}: Parameters<
  NonNullable<Parameters<typeof MaterialTopTabs>[0]['layout']>
>[0]) {
  const lastSegment = useLastSegment();
  const router = useRouter();

  const flatListIndex = useSharedValue(PAGES.indexOf(lastSegment));
  const [currentIndex, setCurrentIndex] = useState(PAGES.indexOf(lastSegment));

  const [personaImport, setPersonaImport] = useState<PersonaImport>({
    acceptedTerms: false,
    uploadStatus: 'idle'
  });

  const updatePersonaImport = useCallback((props: Partial<PersonaImport>) => {
    setPersonaImport((prev) => ({ ...prev, ...props }));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const reset = useCallback(() => {
    updatePersonaImport({
      uploadStatus: 'idle'
    });

    router.replace('/persona-import/upload');
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const onNext = useCallback(async () => {
    const nextSegment = PAGES[flatListIndex.get() + 1];
    setCurrentIndex(PAGES.indexOf(lastSegment));
    if (nextSegment === 'how') {
      if (personaImport.acceptedTerms) {
        router.navigate('/persona-import/how');
      } else {
        alert('Please accept the terms and conditions.');
      }
    } else if (nextSegment === 'upload') {
      router.navigate('/persona-import/upload');
    } else if (nextSegment === 'preview') {
      if (personaImport.uploadStatus === 'idle') {
        alert('Please begin uploading your recording.');
      } else if (personaImport.uploadStatus === 'completed') {
        if (personaImport.personaImportId) {
          router.push(
            `/persona-import/preview/${personaImport.personaImportId}`
          );
        } else {
          alert('Please go back and try again');
        }
      } else {
        alert('Please wait for the upload to complete.');
      }
    } else {
      alert('End!');
    }
  }, [
    personaImport.acceptedTerms,
    personaImport.uploadStatus,
    personaImport.personaImportId,
    personaImport.name,
    personaImport.otherParticipantName,
    personaImport.relationship,
    lastSegment
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    setCurrentIndex(PAGES.indexOf(lastSegment));
    flatListIndex.set(PAGES.indexOf(lastSegment));
  }, [lastSegment]);

  return (
    <PersonaImportContext.Provider
      value={{ personaImport, updatePersonaImport, reset, onNext }}
    >
      {children}

      {['preview', 'process'].includes(lastSegment) ? null : (
        <View className="m-5 flex-row items-center justify-between">
          <OnboardingPagination
            dataLength={PAGES.length}
            currentIndex={currentIndex}
          />

          <OnboardingButton
            flatListIndex={flatListIndex}
            dataLength={PAGES.length}
            onLast={onNext}
            onNext={onNext}
            isLoading={false}
          />
        </View>
      )}
    </PersonaImportContext.Provider>
  );
}

function TabBar({
  navigation
}: Parameters<
  NonNullable<Parameters<typeof MaterialTopTabs>[0]['tabBar']>
>[0]) {
  const alertRef = useRef<AlertRef>(null);

  const lastSegment = useLastSegment();
  const router = useRouter();
  const searchParams = useGlobalSearchParams() as { first: '1' | undefined };

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  const goBack = useCallback(() => {
    if (lastSegment === 'preview' || lastSegment === 'process') {
      alertRef.current?.show();
      return;
    }

    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      router.navigate('/app');
    }
  }, [navigation, lastSegment]);

  // TODO: this doesn't _quite_ work, like when resuming an in-progress import.
  // ideally we'd load the number of personas they have and base this on that
  // but for now, it's fine ? it just sends folks in a loop
  const shouldShowBack =
    !searchParams.first ||
    searchParams.first !== '1' ||
    (searchParams.first === '1' && lastSegment !== 'persona-import');

  return (
    <Fragment>
      <Alert
        ref={alertRef}
        title="Discard draft?"
        message="Are you sure you want to discard your Persona? You will lose all progress."
        buttons={[
          {
            text: 'Cancel',
            style: 'cancel'
          },
          {
            text: 'OK',
            onPress: () => {
              router.replace('/persona-import/upload');
            }
          }
        ]}
      />

      <View className="h-12 bg-[#0f1313]">
        <View className="relative flex-row items-center justify-center">
          {shouldShowBack && (
            <HeaderBackButton
              tintColor="#8FF3E7"
              onPress={goBack}
              style={styles.headerBackButton}
            />
          )}
          <Text className="pt-3 text-center font-medium">
            {PAGE_TITLES[lastSegment as keyof typeof PAGE_TITLES]}
          </Text>
        </View>
      </View>
    </Fragment>
  );
}

function useLastSegment() {
  const segments = useSegments();

  let lastSegment = segments[segments.length - 1];
  if (lastSegment === '[id]') {
    lastSegment = segments[segments.length - 2];
  }

  return lastSegment;
}

const styles = StyleSheet.create({
  headerBackButton: {
    position: 'absolute',
    left: 8,
    top: -1,
    padding: 8
  }
});
